import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency, formatPercent, formatMonths } from './formatters';
import SopraLogoImg from '../assets/sopra_steria.png'; // Import the logo

export const generateROIPDF = (formData, results) => {
  const doc = new jsPDF();
  
  // Set default font
  doc.setFont('helvetica');
  
  // Add Sopra Steria logo
  const logoWidth = 29.6;  // Width in mm (calculated: 20 * 600/812)
  const logoHeight = 40; // Height in mm (target)
  const logoX = 30;     // X position
  const logoY = 15;     // Y position
  
  try {
    // Use the imported logo variable
    doc.addImage(SopraLogoImg, 'PNG', logoX, logoY, logoWidth, logoHeight); 
  } catch (error) {
    console.error('Error adding logo:', error);
  }
  
  // Add header - split into two lines
  doc.setFontSize(24);
  doc.setTextColor(35, 31, 32); // Dark gray color
  doc.text('Automation Factory ROI', 70, 30);
  doc.text('Analysis Report', 70, 38); // Second line moved down
  
  // Add date and report ID - adjust Y position
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128); // Gray color
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 70, 46); // Adjusted Y
  doc.text(`Report ID: ${Math.random().toString(36).substr(2, 9)}`, 70, 51); // Adjusted Y
  
  // Add decorative line - adjust Y position
  doc.setDrawColor(219, 29, 43); // Sopra Steria red
  doc.setLineWidth(0.5);
  doc.line(20, 58, 190, 58); // Adjusted Y
  
  // Add summary section - adjust Y position
  doc.setFontSize(18);
  doc.setTextColor(35, 31, 32);
  doc.text('Executive Summary', 20, 70); // Adjusted Y
  
  // Use actual properties from the calculator results
  const totalImplementationCost = results.totalImplementationCost || 0;
  const annualCostSavings = results.annualCostSavings || 0;
  const roi = results.roi || 0;
  const paybackPeriod = results.paybackPeriod || 0;
  const timeSavedAnnually = results.timeSavedAnnually || 0;
  const fteSavings = results.fteSavings || 0;
  const errorReductionSavings = results.errorReductionSavings || 0;

  // Add key metrics in a modern table design
  const metricsData = [
    ['Total Implementation Cost', formatCurrency(totalImplementationCost)],
    ['Annual Cost Savings', formatCurrency(annualCostSavings)],
    ['ROI', formatPercent(roi)],
    ['Payback Period', formatMonths(paybackPeriod)],
    ['Annual Time Saved', `${Math.round(timeSavedAnnually)} hours`],
    ['FTE Savings', `${fteSavings}`],
    ['Error Reduction Savings', formatCurrency(errorReductionSavings)]
  ];

  doc.autoTable({
    startY: 80,
    head: [['Metric', 'Value']],
    body: metricsData,
    theme: 'grid',
    headStyles: { 
      fillColor: [219, 29, 43],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    styles: { 
      fontSize: 10,
      cellPadding: 5
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70 }
    }
  });

  // Add cost breakdown
  doc.setFontSize(18);
  doc.setTextColor(35, 31, 32);
  doc.text('Cost Breakdown', 20, doc.lastAutoTable.finalY + 20);

  // Używaj tylko tych pól, które są dostępne w obiekcie results
  const costData = [
    ['Implementation Cost', formatCurrency(totalImplementationCost)],
    ['Estimated License Cost', formatCurrency(results.licensesCost || 0)],
    ['Annual Maintenance Cost', formatCurrency(results.maintenanceCost || 0)],
  ];

  doc.autoTable({
    startY: doc.lastAutoTable.finalY + 25,
    head: [['Cost Type', 'Amount']],
    body: costData,
    theme: 'grid',
    headStyles: { 
      fillColor: [244, 114, 22],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    styles: { 
      fontSize: 10,
      cellPadding: 5
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70 }
    }
  });

  // Add a new page before Process Details
  doc.addPage();

  // Add process details with modern styling
  doc.setFontSize(18);
  doc.setTextColor(35, 31, 32);
  doc.text('Process Details', 20, 20); // Start at the top of the new page

  // Używaj tylko tych pól, które są dostępne w obiekcie results
  const processData = [
    ['Process Time', `${results.processTime || 0} minutes`],
    ['Automated Time', `${results.automatedTime || 0} minutes`],
    ['Process Frequency', `${results.annualVolume || 0} times per year`],
    ['Error Rate Before', `${results.errorRateBefore || 0}%`],
    ['Error Rate After', '1%'],
  ];

  doc.autoTable({
    startY: 25, // Start table immediately after the heading
    head: [['Parameter', 'Value']],
    body: processData,
    theme: 'grid',
    headStyles: { 
      fillColor: [35, 31, 32],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    styles: { 
      fontSize: 10,
      cellPadding: 5
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70 }
    }
  });

  // Add assumptions and notes with modern styling
  doc.setFontSize(14);
  doc.setTextColor(35, 31, 32);
  doc.text('Assumptions and Notes', 20, doc.lastAutoTable.finalY + 20);
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128);
  
  const notes = [
    '• ROI calculations are based on annual benefits and costs',
    '• Time savings are calculated based on process frequency and automation efficiency',
    '• FTE savings are calculated assuming 2,080 working hours per year',
    '• Error rate after automation is assumed to be 1%',
    '• Maintenance costs are calculated as a percentage of implementation cost',
  ];

  notes.forEach((note, index) => {
    doc.text(note, 20, doc.lastAutoTable.finalY + 30 + (index * 7));
  });

  // Update footer with Sopra Steria branding
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text('© 2025 Sopra Steria. All rights reserved.', 20, 270);
  doc.text('This report is confidential and intended for the recipient only.', 20, 275);

  // Add disclaimer
  doc.setFontSize(10);
  doc.setTextColor(219, 29, 43); // Sopra Steria red color
  const disclaimer = 'DISCLAIMER: This calculator provides a cost simulation and ROI estimation based on the input parameters. The results are for informational purposes only and do not constitute a final offer or binding agreement. Actual costs and savings may vary based on specific project requirements and circumstances.';
  
  // Split text into multiple lines if needed
  const splitText = doc.splitTextToSize(disclaimer, 170);
  doc.text(splitText, 20, 280);

  return doc;
};

export const sendROIReport = async (pdfDoc, email) => {
  try {
    // Convert PDF to base64
    const pdfBase64 = pdfDoc.output('datauristring');
    return pdfBase64;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF report');
  }
}; 